import React from 'react';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  List,
  ListItem,
  ListIcon,
  Collapse,
  Button,
  useDisclosure
} from '@chakra-ui/react';
import { OAuthDiagnostics } from '../utils/oauthDiagnostics';
import { MdErrorOutline, MdCheckCircle } from 'react-icons/md';

interface OAuthErrorFeedbackProps {
  diagnostics: OAuthDiagnostics;
}

/**
 * Component to display user-friendly OAuth error feedback with actionable steps
 */
const OAuthErrorFeedback: React.FC<OAuthErrorFeedbackProps> = ({ diagnostics }) => {
  const { isOpen, onToggle } = useDisclosure();

  if (!diagnostics || !diagnostics.hasError) {
    return null;
  }

  const provider = diagnostics.provider.charAt(0).toUpperCase() + diagnostics.provider.slice(1);

  return (
    <Alert
      status="error"
      variant="subtle"
      flexDirection="column"
      alignItems="start"
      justifyContent="start"
      textAlign="left"
      borderRadius="md"
      p={4}
      mb={4}
    >
      <AlertIcon boxSize="24px" mr={0} />
      <AlertTitle mt={4} mb={1} fontSize="lg">
        {provider} Authentication Error
      </AlertTitle>
      <AlertDescription maxWidth="sm">
        <Box>{diagnostics.message}</Box>
        
        <Button
          onClick={onToggle}
          variant="link"
          colorScheme="blue"
          size="sm"
          mt={2}
          mb={2}
        >
          {isOpen ? 'Hide troubleshooting steps' : 'Show troubleshooting steps'}
        </Button>
        
        <Collapse in={isOpen} animateOpacity>
          <Box
            p={3}
            color="white"
            mt={2}
            bg="gray.800"
            rounded="md"
            shadow="md"
          >
            <List spacing={2}>
              {diagnostics.possibleSolutions.map((solution, index) => (
                <ListItem key={index} display="flex" alignItems="flex-start">
                  <ListIcon as={MdCheckCircle as React.ElementType} color="green.500" mt={1} />
                  <Box>{solution}</Box>
                </ListItem>
              ))}
            </List>
            
            {diagnostics.technicalDetails && (
              <Box mt={3} fontSize="sm" color="gray.400">
                <strong>Technical details:</strong> {diagnostics.technicalDetails}
              </Box>
            )}
          </Box>
        </Collapse>
      </AlertDescription>
    </Alert>
  );
};

export default OAuthErrorFeedback;
